import React, { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Link, Stack, Typography } from '@mui/material';

import { AppContext } from '../../../../context';
import { translate as t } from 'hooks/useTranslations';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import useOrganizationRole from 'views/App/Portal/LexID/Roles/_data_sources/useRole';
import useRoleUsers from 'views/App/Portal/LexID/Roles/_data_sources/useRoleUsers';
import useRoleGroups from 'views/App/Portal/LexID/Roles/_data_sources/useRoleGroups';
import useUrlParams from 'hooks/useUrlParams';
import Overview from './Overview';
import Users from './Users';
import Groups from './Groups';

type RoleTab = {
  label: string;
  key: string;
  component: React.ReactNode;
  count?: number;
};

type RoleUrlParams = {
  tab: string;
};

const defaultParams: RoleUrlParams = {
  tab: 'overview',
};

export default function RoleDetails() {
  const { currentOrganizationId, hasPermission } = useContext(AppContext);

  const { id } = useParams();
  const roleQuery = useOrganizationRole(currentOrganizationId || '', id || '');
  const [params, setParams] = useUrlParams(defaultParams);
  const roleUsersQuery = useRoleUsers(
    currentOrganizationId || '',
    id || '',
    {},
    { enabled: !!hasPermission('listUsersInRole', currentOrganizationId) }
  );
  const roleGroupsQuery = useRoleGroups(
    currentOrganizationId || '',
    id || '',
    {},
    { enabled: !!hasPermission('listGroupsForRole', currentOrganizationId) }
  );

  const tabs: { [key: string]: RoleTab } = {
    overview: {
      label: t('roles.details.tabs.details'),
      key: 'details',
      component: <Overview />,
    },
  };
  if (
    currentOrganizationId &&
    hasPermission('listUsersInRole', currentOrganizationId) &&
    hasPermission('listUsers', currentOrganizationId)
  ) {
    tabs.users = {
      label: t('roles.details.tabs.users'),
      key: 'users',
      component: <Users />,
      count: roleUsersQuery.data?.pages[0]?.count,
    };
  }
  if (
    currentOrganizationId &&
    hasPermission('listGroupsForRole', currentOrganizationId) &&
    hasPermission('listGroups', currentOrganizationId)
  ) {
    tabs.groups = {
      label: t('roles.details.tabs.groups'),
      key: 'groups',
      component: <Groups />,
      count: roleGroupsQuery.data?.groups?.length,
    };
  }

  const updateParams = React.useCallback(
    (updatedParams: RoleUrlParams) => {
      setParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const breadcrumbItems = [
    <Link
      key='b1'
      href='..'
    >
      {t('roles.title')}
    </Link>,
    <Typography key='b4'>{roleQuery.data?.name || ''}</Typography>,
  ];

  const editButton =
    currentOrganizationId && hasPermission('updateRole', currentOrganizationId) ? (
      <Button
        variant='contained'
        size='small'
        href={`../edit/${roleQuery.data?.id}`}
      >
        {t('roles.actions.edit')}
      </Button>
    ) : null;

  return (
    <CommonLayout
      headerProps={{
        title: roleQuery.data?.name || '',
        breadcrumbItems,
        actions: editButton,
      }}
      tabs={{
        items: Object.entries(tabs).map(([key, tab]) => ({
          label: tab.label,
          value: key,
          count: tab.count,
        })),
        value: params.tab,
        onChange: (event: React.SyntheticEvent, newValue: string | number) =>
          updateParams({ tab: newValue.toString() }),
      }}
    >
      {tabs[params.tab]?.component}
    </CommonLayout>
  );
}
